import {
  AppCredentialConfig,
  ClientPlatform,
  CLIENT_PLATFORM_MAP,
  MerchantConfig,
  PlatformPaymentConfig,
  PlatformPaymentMethod,
  PaymentProvider,
  PlatformPaymentRoute,
  PLATFORM_PAYMENT_METHOD_MAP,
  PAYMENT_PROVIDER_MAP,
} from '../../types';
import {
  analyzeRouteScopeStatus,
  buildAppCredentialSelectOptions,
  buildMerchantSelectOptions,
  findAppCredentialConfig,
  findExpectedActiveRouteId,
  findMerchantConfig,
  formatAppCredentialLabel,
  formatMerchantLabel,
  getRouteScopeIssue,
  idsEqual,
  isRouteConfigCompleteWithPools,
  mergeSelectOptions,
  resolveRouteScopeKey,
  toNumberId,
} from '../../utils/paymentConfig';

export const PLATFORM_OPTIONS: ClientPlatform[] = ['WEAPP', 'WECHAT_H5', 'H5', 'ALIPAY'];

export const PLATFORM_FILTER_OPTIONS = [
  { label: '全部', value: '' },
  ...PLATFORM_OPTIONS.map(platform => ({
    label: CLIENT_PLATFORM_MAP[platform],
    value: platform,
  })),
];

export const ROUTE_EDIT_LOCKED_FIELDS = ['clientPlatform', 'paymentMethod', 'paymentProvider'] as const;

export type RouteFilters = {
  clientPlatform?: ClientPlatform;
  appCredentialId?: number;
  merchantConfigId?: number;
};

export type RouteSelection = Partial<
  Pick<
    PlatformPaymentRoute,
    'paymentMethod' | 'paymentProvider' | 'appCredentialId' | 'merchantConfigId'
  >
>;

const unique = <T,>(items: T[]) => Array.from(new Set(items));

const PLATFORM_SORT_ORDER: Record<ClientPlatform, number> = {
  WEAPP: 0,
  WECHAT_H5: 1,
  H5: 2,
  ALIPAY: 3,
};

export const getProviderLabel = (provider?: string) => {
  if (!provider) {
    return '-';
  }
  return PAYMENT_PROVIDER_MAP[provider as keyof typeof PAYMENT_PROVIDER_MAP] || provider;
};

/** 云汇腾首期仅允许微信小程序 + 微信小程序支付 */
export const isYunhuitengRouteAllowed = (
  platform?: ClientPlatform,
  method?: PlatformPaymentMethod,
  provider?: PaymentProvider,
): boolean => {
  if (provider !== 'YUNHUITENG') {
    return true;
  }
  return platform === 'WEAPP' && method === 'WECHAT_MINIAPP';
};

export const filterProvidersForRouteSelection = (
  platform: ClientPlatform | undefined,
  method: PlatformPaymentMethod | undefined,
  providers: PaymentProvider[],
): PaymentProvider[] =>
  providers.filter(provider => isYunhuitengRouteAllowed(platform, method, provider));

export const flattenPlatformRoutes = (platforms: PlatformPaymentConfig[]): PlatformPaymentRoute[] =>
  platforms.flatMap(platform => platform.candidateRoutes || []);

export const filterPaymentRoutes = (
  routes: PlatformPaymentRoute[],
  filters: RouteFilters,
): PlatformPaymentRoute[] =>
  routes.filter(route => {
    if (filters.clientPlatform && route.clientPlatform !== filters.clientPlatform) {
      return false;
    }
    if (
      filters.appCredentialId !== undefined &&
      !idsEqual(route.appCredentialId, filters.appCredentialId)
    ) {
      return false;
    }
    if (
      filters.merchantConfigId !== undefined &&
      !idsEqual(route.merchantConfigId, filters.merchantConfigId)
    ) {
      return false;
    }
    return true;
  });

export const sortPaymentRoutes = (
  routes: PlatformPaymentRoute[],
  appCredentials: AppCredentialConfig[],
): PlatformPaymentRoute[] =>
  [...routes].sort((left, right) => {
    const platformDiff =
      (PLATFORM_SORT_ORDER[left.clientPlatform] ?? 99) -
      (PLATFORM_SORT_ORDER[right.clientPlatform] ?? 99);
    if (platformDiff !== 0) {
      return platformDiff;
    }
    const methodLeft = PLATFORM_PAYMENT_METHOD_MAP[left.paymentMethod] || left.paymentMethod;
    const methodRight = PLATFORM_PAYMENT_METHOD_MAP[right.paymentMethod] || right.paymentMethod;
    const methodDiff = methodLeft.localeCompare(methodRight, 'zh-CN');
    if (methodDiff !== 0) {
      return methodDiff;
    }
    const leftCredential = findAppCredentialConfig(left.appCredentialId, appCredentials);
    const rightCredential = findAppCredentialConfig(right.appCredentialId, appCredentials);
    const credentialDiff = formatAppCredentialLabel(
      leftCredential || { appCredentialId: toNumberId(left.appCredentialId) || 0, appName: '', appId: '' },
    ).localeCompare(
      formatAppCredentialLabel(
        rightCredential || { appCredentialId: toNumberId(right.appCredentialId) || 0, appName: '', appId: '' },
      ),
      'zh-CN',
    );
    if (credentialDiff !== 0) {
      return credentialDiff;
    }
    return (left.sortOrder ?? 0) - (right.sortOrder ?? 0);
  });

export const getEnableRuleAlertMessage = (platform?: ClientPlatform): string => {
  if (platform === 'WEAPP' || platform === 'ALIPAY') {
    return '微信小程序、支付宝小程序：同一客户端平台和真实 AppID 仅一条配置可启用（配置已启用）。是否当前可执行还取决于服务商运行门禁。';
  }
  if (platform === 'WECHAT_H5' || platform === 'H5') {
    return '微信 H5、普通 H5：同一客户端平台仅一条配置可启用（配置已启用）。是否当前可执行还取决于服务商运行门禁。';
  }
  return '启用规则：同一客户端平台和真实 AppID 仅一条配置启用；enabled 仅表示配置已启用，不等于当前生产可执行。保存后仅影响新订单。';
};

export const getRouteEnabledStatusLabel = (
  route: PlatformPaymentRoute,
  scopeStatus: ReturnType<typeof analyzeRouteScopeStatus>,
): { text: string; color: string } => {
  const issue = getRouteScopeIssue(route, scopeStatus);
  if (issue === 'conflict') {
    return { text: '路由冲突', color: 'error' };
  }
  if (issue === 'empty_scope') {
    return { text: '作用域缺失', color: 'error' };
  }
  if (route.enabled) {
    return { text: '配置已启用', color: 'green' };
  }
  return { text: '未启用', color: 'default' };
};

export const filterAppCredentialsForPlatform = (
  credentials: AppCredentialConfig[],
  platform?: ClientPlatform,
): AppCredentialConfig[] => {
  if (!platform) {
    return credentials;
  }
  return credentials.filter(item => item.clientPlatform === platform);
};

export const findPlatformConfig = (
  platforms: PlatformPaymentConfig[],
  platform: ClientPlatform,
): PlatformPaymentConfig | undefined => platforms.find(item => item.clientPlatform === platform);

export const getCompatibleRouteOptions = (
  config?: PlatformPaymentConfig,
  selection: RouteSelection = {},
  global?: {
    appCredentialConfigs?: AppCredentialConfig[];
    merchantConfigs?: MerchantConfig[];
  },
) => {
  const routes = config?.candidateRoutes || [];
  const capabilities = config?.capabilities?.length
    ? config.capabilities
    : (config?.supportedMethods || []).map(method => ({
        clientPlatform: config?.clientPlatform as ClientPlatform,
        paymentMethod: method,
        providers: unique(
          routes
            .filter(route => route.paymentMethod === method)
            .map(route => route.paymentProvider),
        ),
      }));
  const routeMatches = (route: PlatformPaymentRoute, keys: Array<keyof RouteSelection>) =>
    keys.every(key => {
      const expected = selection[key];
      if (expected === undefined) {
        return true;
      }
      if (key === 'appCredentialId' || key === 'merchantConfigId') {
        return idsEqual(route[key], expected);
      }
      return route[key] === expected;
    });

  const paymentMethods = unique(capabilities.map(capability => capability.paymentMethod));
  const selectedCapability = capabilities.find(capability => capability.paymentMethod === selection.paymentMethod);
  const paymentProviders = filterProvidersForRouteSelection(
    config?.clientPlatform,
    selection.paymentMethod,
    selectedCapability?.providers || [],
  );
  const selectedProvider = selection.paymentProvider;
  const providerMatchesCapability = (provider: PaymentProvider) =>
    !selectedProvider
      ? paymentProviders.includes(provider)
      : paymentProviders.includes(selectedProvider) && provider === selectedProvider;

  const matchedRoute =
    selection.paymentMethod &&
    selection.paymentProvider &&
    selection.appCredentialId &&
    selection.merchantConfigId
      ? routes.find(route =>
          routeMatches(route, [
            'paymentMethod',
            'paymentProvider',
            'appCredentialId',
            'merchantConfigId',
          ]),
        )
      : undefined;

  const credentialPool =
    global?.appCredentialConfigs && global.appCredentialConfigs.length > 0
      ? global.appCredentialConfigs
      : config?.appCredentialConfigs || [];
  const merchantPool =
    global?.merchantConfigs && global.merchantConfigs.length > 0
      ? global.merchantConfigs
      : config?.merchantConfigs || [];

  const appCredentialConfigs = credentialPool.filter(
    item =>
      item.clientPlatform === config?.clientPlatform &&
      (!item.paymentProvider || providerMatchesCapability(item.paymentProvider)),
  );
  const merchantConfigs = merchantPool.filter(item => providerMatchesCapability(item.paymentProvider));

  return {
    paymentMethods,
    paymentProviders,
    appCredentialConfigs: mergeSelectOptions(
      appCredentialConfigs,
      credentialPool,
      selection.appCredentialId,
      item => item.appCredentialId,
    ),
    merchantConfigs: mergeSelectOptions(
      merchantConfigs,
      merchantPool,
      selection.merchantConfigId,
      item => item.merchantConfigId,
    ),
    matchedRoute,
  };
};

export const normalizeRouteSelection = (
  route?: PlatformPaymentRoute,
  fallbackMethod?: PlatformPaymentMethod,
): RouteSelection => {
  if (!route) {
    return fallbackMethod ? { paymentMethod: fallbackMethod } : {};
  }
  return {
    paymentMethod: route.paymentMethod,
    paymentProvider: route.paymentProvider,
    appCredentialId: toNumberId(route.appCredentialId),
    merchantConfigId: toNumberId(route.merchantConfigId),
  };
};

export const sanitizeRouteSelection = (
  selection: RouteSelection,
  appCredentials: AppCredentialConfig[],
  merchants: MerchantConfig[],
): RouteSelection => {
  const next: RouteSelection = { ...selection };
  const appCredentialValid =
    next.appCredentialId === undefined ||
    appCredentials.some(item => idsEqual(item.appCredentialId, next.appCredentialId));
  const merchantValid =
    next.merchantConfigId === undefined ||
    merchants.some(item => idsEqual(item.merchantConfigId, next.merchantConfigId));

  if (!appCredentialValid) {
    delete next.appCredentialId;
    delete next.merchantConfigId;
  } else if (!merchantValid) {
    delete next.merchantConfigId;
  }

  return next;
};

export const buildRouteFromSelection = (
  config?: PlatformPaymentConfig,
  selection: RouteSelection = {},
  global?: {
    appCredentialConfigs?: AppCredentialConfig[];
    merchantConfigs?: MerchantConfig[];
  },
  options?: { enabled?: boolean; displayName?: string },
): PlatformPaymentRoute | undefined => {
  if (
    !config ||
    !selection.paymentMethod ||
    !selection.paymentProvider ||
    !selection.appCredentialId ||
    !selection.merchantConfigId
  ) {
    return undefined;
  }

  const matchedRoute = config.candidateRoutes.find(
    route =>
      route.paymentMethod === selection.paymentMethod &&
      route.paymentProvider === selection.paymentProvider &&
      idsEqual(route.appCredentialId, selection.appCredentialId) &&
      idsEqual(route.merchantConfigId, selection.merchantConfigId),
  );
  const routeOptions = getCompatibleRouteOptions(config, selection, global);
  const isValidSelection =
    routeOptions.paymentProviders.includes(selection.paymentProvider) &&
    isYunhuitengRouteAllowed(config.clientPlatform, selection.paymentMethod, selection.paymentProvider) &&
    routeOptions.appCredentialConfigs.some(item => idsEqual(item.appCredentialId, selection.appCredentialId)) &&
    routeOptions.merchantConfigs.some(item => idsEqual(item.merchantConfigId, selection.merchantConfigId));

  if (!isValidSelection) {
    return undefined;
  }

  const route: PlatformPaymentRoute = {
    routeId: matchedRoute?.routeId,
    clientPlatform: config.clientPlatform,
    paymentMethod: selection.paymentMethod,
    paymentProvider: selection.paymentProvider,
    appCredentialId: selection.appCredentialId,
    merchantConfigId: selection.merchantConfigId,
    enabled: options?.enabled ?? matchedRoute?.enabled ?? true,
  };
  const displayName = options?.displayName ?? matchedRoute?.displayName;
  if (displayName) {
    route.displayName = displayName;
  }
  if (matchedRoute?.sortOrder !== undefined) {
    route.sortOrder = matchedRoute.sortOrder;
  }
  return route;
};

export const buildRouteSavePayload = (
  route: PlatformPaymentRoute,
  patch: Partial<Pick<PlatformPaymentRoute, 'appCredentialId' | 'merchantConfigId' | 'enabled' | 'displayName'>>,
  options?: {
    allRoutes?: PlatformPaymentRoute[];
    appCredentials?: AppCredentialConfig[];
  },
): PlatformPaymentRoute => {
  const enabled = patch.enabled ?? route.enabled;
  const next: PlatformPaymentRoute = {
    routeId: route.routeId,
    clientPlatform: route.clientPlatform,
    paymentMethod: route.paymentMethod,
    paymentProvider: route.paymentProvider,
    appCredentialId: patch.appCredentialId ?? route.appCredentialId,
    merchantConfigId: patch.merchantConfigId ?? route.merchantConfigId,
    enabled,
    displayName: patch.displayName ?? route.displayName,
    sortOrder: route.sortOrder,
    routeScopeKey: route.routeScopeKey,
    routeVersion: route.routeVersion,
  };
  if (enabled && options?.allRoutes && options.appCredentials) {
    next.expectedActiveRouteId = findExpectedActiveRouteId(
      options.allRoutes,
      next,
      options.appCredentials,
    );
  }
  return next;
};

export const withExpectedActiveRouteId = (
  route: PlatformPaymentRoute,
  allRoutes: PlatformPaymentRoute[],
  appCredentials: AppCredentialConfig[],
): PlatformPaymentRoute => {
  if (!route.enabled) {
    return route;
  }
  return {
    ...route,
    expectedActiveRouteId: findExpectedActiveRouteId(allRoutes, route, appCredentials),
  };
};

export {
  analyzeRouteScopeStatus,
  getRouteScopeIssue,
  resolveRouteScopeKey,
};

export const isRouteConfigComplete = (
  route?: PlatformPaymentRoute,
  pools?: {
    appCredentialConfigs?: AppCredentialConfig[];
    merchantConfigs?: MerchantConfig[];
  },
) => {
  if (!route) {
    return false;
  }
  return isRouteConfigCompleteWithPools(
    route,
    pools?.appCredentialConfigs || [],
    pools?.merchantConfigs || [],
  );
};

export const formatRouteAppCredentialLabel = (
  id: unknown,
  pool: AppCredentialConfig[],
): { label: string; invalid: boolean } => {
  const config = findAppCredentialConfig(id, pool);
  if (config) {
    return { label: formatAppCredentialLabel(config), invalid: false };
  }
  const numericId = toNumberId(id);
  if (numericId === undefined) {
    return { label: '-', invalid: true };
  }
  return { label: `无效引用 #${numericId}`, invalid: true };
};

export const formatRouteMerchantLabel = (
  id: unknown,
  pool: MerchantConfig[],
): { label: string; invalid: boolean } => {
  const config = findMerchantConfig(id, pool);
  if (config) {
    return { label: formatMerchantLabel(config), invalid: false };
  }
  const numericId = toNumberId(id);
  if (numericId === undefined) {
    return { label: '-', invalid: true };
  }
  return { label: `无效引用 #${numericId}`, invalid: true };
};

export { buildAppCredentialSelectOptions, buildMerchantSelectOptions };
